<script lang="ts">
  import { untrack } from "svelte";
  import type { DealCard, DealField, DealFieldValue, DealPatch } from "../lib/api";
  import { haptic } from "../lib/tg";
  import type { TeamMember } from "./TaskCreateSheet.svelte";

  let {
    open = $bindable(false),
    deal,
    fields = [] as DealField[],
    team = [] as TeamMember[],
    canReassign = false,
    onsave,
  }: {
    open?: boolean;
    deal: DealCard;
    fields?: DealField[];
    team?: TeamMember[];
    canReassign?: boolean;
    onsave?: (patch: DealPatch) => Promise<boolean>;
  } = $props();

  const DATE_TYPES = new Set(["date", "birthday", "date_time"]);
  const MULTI_LINE = new Set(["textarea"]);

  // Form state — strings for inputs, number[] for (multi)selects.
  let name = $state("");
  let price = $state("");
  let responsible = $state<number | null>(null);
  let values = $state<Record<string, string | number[]>>({});
  let initial: { name: string; price: string; responsible: number | null; values: Record<string, string | number[]> } =
    { name: "", price: "", responsible: null, values: {} };
  let saving = $state(false);

  function tsToDate(v: unknown): string {
    const n = Number(v);
    if (!Number.isFinite(n) || n <= 0) return "";
    // amoCRM stores dates as 00:00 MSK → read the MSK calendar day.
    return new Date((n + 3 * 3600) * 1000).toISOString().slice(0, 10);
  }

  function fromMirror(f: DealField): string | number[] {
    const raw = (deal.custom_fields?.[f.id] ?? []).filter((v) => v !== null && v !== undefined && String(v) !== "");
    if (f.type === "select" || f.type === "radiobutton" || f.type === "multiselect") {
      return raw
        .map((v) => f.enums.find((e) => e.value === String(v))?.id)
        .filter((x): x is number => x !== undefined);
    }
    if (DATE_TYPES.has(f.type)) return raw.length ? tsToDate(raw[0]) : "";
    return raw.length ? String(raw[0]) : "";
  }

  // Seed the form only when the sheet opens — a background card refresh
  // (voice/task polling) must not wipe what the user is typing.
  $effect(() => {
    if (open) untrack(seed);
  });

  function seed() {
    const v: Record<string, string | number[]> = {};
    for (const f of fields) v[f.id] = fromMirror(f);
    initial = {
      name: deal.name,
      price: deal.price ? String(deal.price) : "",
      responsible: deal.responsible_user_amo_id ?? null,
      values: JSON.parse(JSON.stringify(v)),
    };
    name = initial.name;
    price = initial.price;
    responsible = initial.responsible;
    values = v;
  }

  function toggleEnum(f: DealField, id: number) {
    const cur = (values[f.id] as number[]) ?? [];
    if (f.type === "multiselect") {
      values[f.id] = cur.includes(id) ? cur.filter((x) => x !== id) : [...cur, id];
    } else {
      values[f.id] = cur[0] === id ? [] : [id];
    }
  }

  function buildPatch(): DealPatch {
    const patch: DealPatch = {};
    if (name.trim() && name.trim() !== initial.name) patch.name = name.trim();
    if (deal.price !== null) {
      const p = price.replace(/\s/g, "");
      if (p !== initial.price) patch.price = p ? Math.max(0, Math.round(Number(p))) : 0;
    }
    if (canReassign && responsible && responsible !== initial.responsible) {
      patch.responsible_user_amo_id = responsible;
    }
    const cf: Record<string, DealFieldValue> = {};
    for (const f of fields) {
      const now = values[f.id];
      if (JSON.stringify(now) === JSON.stringify(initial.values[f.id])) continue;
      if (Array.isArray(now)) {
        cf[f.id] = f.type === "multiselect" ? now : (now[0] ?? null);
      } else {
        cf[f.id] = now.trim() ? now.trim() : null;
      }
    }
    if (Object.keys(cf).length) patch.custom_fields = cf;
    return patch;
  }

  const dirty = $derived.by(() => {
    // re-run on any input change
    void name; void price; void responsible; void JSON.stringify(values);
    return open ? Object.keys(buildPatch()).length > 0 : false;
  });

  async function save() {
    const patch = buildPatch();
    if (!Object.keys(patch).length || !onsave) return;
    if (deal.price !== null && patch.price !== undefined && Number.isNaN(patch.price)) return;
    haptic("medium");
    saving = true;
    try {
      if (await onsave(patch)) open = false;
    } finally {
      saving = false;
    }
  }
</script>

{#if open}
  <div class="backdrop" onclick={() => !saving && (open = false)} role="presentation"></div>
  <div class="sheet" role="dialog" aria-label="Редактирование сделки">
    <div class="grab"></div>
    <div class="title">Изменить сделку</div>

    <label class="lbl" for="de-name">Название</label>
    <input id="de-name" class="inp" bind:value={name} maxlength="255" />

    {#if deal.price !== null}
      <label class="lbl" for="de-price">Бюджет, ₽</label>
      <input id="de-price" class="inp mono" inputmode="numeric" bind:value={price} placeholder="0" />
    {/if}

    {#if canReassign && team.length}
      <div class="lbl">Ответственный</div>
      <div class="chips">
        {#each team as t}
          <button class="chip" class:active={responsible === t.amo_id}
                  onclick={() => (responsible = t.amo_id)} type="button">{t.name}</button>
        {/each}
      </div>
    {/if}

    {#each fields as f (f.id)}
      {#if f.type === "select" || f.type === "radiobutton" || f.type === "multiselect"}
        <div class="lbl">{f.label}{#if f.type === "multiselect"}<span class="hint"> · несколько</span>{/if}</div>
        <div class="chips">
          {#each f.enums as e}
            <button class="chip" class:active={((values[f.id] as number[]) ?? []).includes(e.id)}
                    onclick={() => toggleEnum(f, e.id)} type="button">{e.value}</button>
          {/each}
        </div>
      {:else if DATE_TYPES.has(f.type)}
        <label class="lbl" for={`de-${f.id}`}>{f.label}</label>
        <div class="row">
          <input id={`de-${f.id}`} class="inp" type="date" bind:value={values[f.id] as string} />
          {#if values[f.id]}
            <button class="clear" onclick={() => (values[f.id] = "")} type="button" aria-label="Очистить">✕</button>
          {/if}
        </div>
      {:else if MULTI_LINE.has(f.type)}
        <label class="lbl" for={`de-${f.id}`}>{f.label}</label>
        <textarea id={`de-${f.id}`} class="inp" rows="2" bind:value={values[f.id] as string}></textarea>
      {:else}
        <label class="lbl" for={`de-${f.id}`}>{f.label}</label>
        <input id={`de-${f.id}`} class="inp" class:mono={f.type === "numeric"}
               inputmode={f.type === "numeric" ? "decimal" : "text"} bind:value={values[f.id] as string} />
      {/if}
    {/each}

    <div class="actions">
      <button class="btn ghost" onclick={() => (open = false)} disabled={saving} type="button">Отмена</button>
      <button class="btn primary" onclick={save} disabled={!dirty || saving} type="button">
        {saving ? "Сохраняю…" : "Сохранить"}
      </button>
    </div>
  </div>
{/if}

<style>
  .backdrop { position: fixed; inset: 0; background: rgba(0,0,0,0.5); z-index: 60; animation: fade 160ms ease; }
  .sheet {
    position: fixed; left: 0; right: 0; bottom: 0;
    background: var(--bg-elev);
    border-top-left-radius: 22px; border-top-right-radius: 22px;
    padding: 12px 18px 0;
    box-shadow: var(--shadow-lg);
    z-index: 70;
    animation: slide 220ms cubic-bezier(.2,.8,.2,1);
    max-height: 90vh; overflow-y: auto;
  }
  .grab { width: 36px; height: 4px; background: var(--border-strong); border-radius: 2px; margin: 0 auto 12px; }
  .title { font-family: 'Manrope', sans-serif; font-weight: 700; font-size: 15px; margin-bottom: 4px; }
  .lbl {
    display: block;
    font-size: 11px; color: var(--text-dim);
    text-transform: uppercase; letter-spacing: 0.08em; font-weight: 600;
    margin-top: 14px; margin-bottom: 6px;
  }
  .hint { text-transform: none; letter-spacing: 0; font-weight: 400; }
  .inp {
    width: 100%; box-sizing: border-box;
    background: var(--bg-elev-2); border: 1px solid var(--border); border-radius: 10px;
    padding: 10px 12px; color: var(--text); font-family: inherit; font-size: 16px; /* ≥16px: no iOS zoom */
  }
  .inp:focus { border-color: var(--ember); outline: none; }
  textarea.inp { resize: vertical; min-height: 56px; }
  .mono { font-family: 'JetBrains Mono', monospace; }
  .row { display: flex; gap: 8px; align-items: center; }
  .clear {
    flex-shrink: 0; width: 40px; height: 40px; border-radius: 10px;
    background: var(--bg-elev-2); border: 1px solid var(--border); color: var(--text-mute); cursor: pointer;
  }
  .chips { display: flex; gap: 6px; flex-wrap: wrap; }
  .chip {
    background: var(--bg-elev-2); border: 1px solid var(--border); border-radius: 10px;
    padding: 8px 12px; color: var(--text); font-size: 13px; cursor: pointer; text-align: left;
    transition: background 120ms, border-color 120ms, transform 100ms;
  }
  .chip:active { transform: scale(0.96); }
  .chip.active { background: var(--ember); color: #fff; border-color: var(--ember); font-weight: 600; }
  .actions {
    position: sticky; bottom: 0;
    display: grid; grid-template-columns: 1fr 1.6fr; gap: 8px;
    margin: 18px -18px 0; padding: 12px 18px calc(12px + var(--safe-bottom, 0px));
    background: var(--bg-elev); border-top: 1px solid var(--border);
  }
  .btn { height: 46px; border-radius: 12px; font-family: 'Manrope', sans-serif; font-weight: 600; font-size: 14px; cursor: pointer; }
  .btn:active { filter: brightness(0.92); }
  .btn.ghost { background: var(--bg-elev-2); color: var(--text); border: 1px solid var(--border); }
  .btn.primary { background: var(--ember); color: #fff; border: none; }
  .btn:disabled { opacity: 0.4; cursor: not-allowed; }
  @keyframes fade { from { opacity: 0; } to { opacity: 1; } }
  @keyframes slide { from { transform: translateY(100%); } to { transform: translateY(0); } }
  @media (prefers-reduced-motion: reduce) { .sheet, .backdrop { animation: none; } }
</style>
